import { Info } from 'lucide-react'
import { DonutChart } from './DonutChart'
import type { QuoteResult } from '../types'
import type { DiscountCategory } from '@/types'

const DISCOUNT_LABELS: Record<DiscountCategory, string> = {
  none: '',
  student: 'Student discount (10%)',
  nonprofit: '501(c)(3) Nonprofit discount (10%)',
  odd_artist: 'ODD Artist discount (20%)',
}

const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

interface QuoteSectionProps {
  quote: QuoteResult
  discountCategory: DiscountCategory
}

export function QuoteSection({ quote, discountCategory }: QuoteSectionProps) {
  const hasLines = quote.lines.length > 0

  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold">Estimate</h2>
      <div className="rounded-xl border bg-card p-5">
        <div className="flex flex-col gap-6">
          {/* Line items */}
          <div className="flex-1 space-y-2">
            {!hasLines ? (
              <p className="text-sm text-muted-foreground">
                Add a service above to see your estimate.
              </p>
            ) : (
              <>
                {quote.lines.map((line, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: line.color }}
                      />
                      <span className={line.isAddon ? 'ml-3 text-muted-foreground truncate' : 'truncate'}>
                        {line.label}
                      </span>
                    </div>
                    <span className={`font-mono shrink-0${line.isInquiry ? ' text-muted-foreground' : ''}`}>
                      {line.isInquiry ? (line.displayPrice ?? 'TBD') : fmt.format(line.amount)}
                    </span>
                  </div>
                ))}

                {discountCategory !== 'none' && (
                  <div className="flex items-center justify-between gap-4 text-sm text-green-600 dark:text-green-400 border-t pt-2 mt-2">
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-green-500" />
                      <span>{DISCOUNT_LABELS[discountCategory]}</span>
                    </div>
                    <span className="font-mono shrink-0">
                      -{fmt.format(quote.discountAmount)}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between gap-4 border-t pt-2 mt-2">
                  <span className="text-sm font-semibold">Estimated Total</span>
                  <span className="font-mono font-semibold">{quote.formattedTotal}</span>
                </div>

                {quote.hasInquiryItems && (
                  <div className="flex items-start gap-2 rounded-md bg-muted/50 border px-3 py-2 text-xs text-muted-foreground mt-2">
                    <Info className="size-3.5 shrink-0 mt-0.5" />
                    <span>
                      One or more services require a custom quote. Your estimate above reflects any fixed-price
                      items only. We'll follow up with full pricing after reviewing your request.
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Donut chart */}
          <div className="flex flex-col items-center gap-2">
            <DonutChart lines={quote.lines.filter((l) => !l.isInquiry)} size={160} thickness={32} />
            {hasLines && (
              <p className="text-xs text-muted-foreground font-mono">{quote.formattedTotal}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
